import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Users, ArrowUpRight, User, Receipt, Pencil, RefreshCw, UserRound, UserPen, Package, Tag, Ship, KeyRound, UserPlus, UserMinus, CheckCircle2, Ban, Coins, ScanLine, Dot } from "lucide-react";
import { NavLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";

/**
 * Beem — Live team activity feed (director only).
 *
 * Fetches recent audit_log events and shows who did what.
 * Uses a dropdown popover mounted next to the notification bell.
 * Only visible to admins/directors.
 */
const ACTION_ICONS = {
  crear_pedido: { icon: Receipt, label: "creó un pedido" },
  actualizar_pedido: { icon: Pencil, label: "actualizó un pedido" },
  cambiar_estado_pedido: { icon: RefreshCw, label: "cambió estado de pedido" },
  crear_cliente: { icon: UserRound, label: "creó un cliente" },
  actualizar_cliente: { icon: UserPen, label: "actualizó un cliente" },
  crear_producto: { icon: Package, label: "creó un producto" },
  actualizar_producto: { icon: Tag, label: "actualizó un producto" },
  importar_contenedor: { icon: Ship, label: "recibió un contenedor" },
  actualizar_permisos: { icon: KeyRound, label: "cambió permisos" },
  crear_usuario: { icon: UserPlus, label: "agregó un miembro" },
  eliminar_usuario: { icon: UserMinus, label: "quitó un miembro" },
  aprobar_excepcion: { icon: CheckCircle2, label: "aprobó una excepción" },
  rechazar_excepcion: { icon: Ban, label: "rechazó una excepción" },
  crear_regla_comision: { icon: Coins, label: "creó una regla de comisión" },
  actualizar_regla_comision: { icon: Coins, label: "actualizó una regla" },
  procesar_ocr: { icon: ScanLine, label: "procesó una remisión OCR" },
};

function labelFor(action) {
  return ACTION_ICONS[action] || { icon: Dot, label: action.replace(/_/g, " ") };
}

export default function ActivityFeed() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const wrap = useRef(null);
  const isDirector = user?.role === "admin";

  const load = async () => {
    if (!isDirector) return;
    try {
      const { data: resp } = await api.get("/audit-log?limit=25"); const data = resp.items || resp;
      setItems(data || []);
    } catch {}
  };

  useEffect(() => {
    if (!isDirector) return;
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [isDirector]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!isDirector) return null;

  // Group by user + hour for a "team pulse" summary at top
  const uniqueActors = [...new Set(items.map((x) => x.actor_name).filter(Boolean))];

  return (
    <div ref={wrap} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-full border border-border/60 bg-card/70 backdrop-blur-sm hover:bg-accent transition-all flex items-center justify-center group"
        data-testid="activity-feed-btn"
        aria-label="Actividad del equipo"
        title="Actividad del equipo"
      >
        <Activity className="w-[17px] h-[17px] text-foreground/70 group-hover:text-primary" strokeWidth={1.8} />
        {items.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-background pulse-dot" title="En vivo" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-[420px] max-h-[560px] bg-card border border-border rounded-2xl shadow-soft-lift z-50 flex flex-col overflow-hidden" data-testid="activity-dropdown">
          <div className="px-4 py-3 border-b border-border/60">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-primary" /> Pulso del equipo
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {items.length === 0 ? "Sin actividad reciente" : `${items.length} eventos · ${uniqueActors.length} personas`}
                </div>
              </div>
            </div>
            {uniqueActors.length > 0 && (
              <div className="flex -space-x-2 mt-2">
                {uniqueActors.slice(0, 6).map((name) => (
                  <div key={name} title={name}
                    className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 text-primary flex items-center justify-center text-[10px] font-semibold border-2 border-card font-display italic">
                    {(name || "?").slice(0, 1).toUpperCase()}
                  </div>
                ))}
                {uniqueActors.length > 6 && (
                  <div className="w-7 h-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[10px] font-medium border-2 border-card">+{uniqueActors.length - 6}</div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground">
                <Users className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Cuando el equipo empiece a trabajar aparecerá aquí.
              </div>
            )}
            {items.map((ev) => {
              const meta = labelFor(ev.action);
              return (
                <div key={ev.id || `${ev.at}-${ev.actor_id}`} className="px-4 py-2.5 border-b border-border/40 last:border-0 hover:bg-accent/30 transition-colors" data-testid={`activity-${ev.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 text-primary flex items-center justify-center text-sm shrink-0 mt-0.5 font-display italic">
                      {(ev.actor_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] leading-tight">
                        <span className="font-semibold text-foreground">{ev.actor_name || "Sistema"}</span>
                        <span className="text-muted-foreground"> {meta.label}</span>
                      </div>
                      {ev.entity_type && ev.entity_id && (
                        <div className="text-[10px] text-muted-foreground/70 truncate mt-0.5">
                          <code className="text-[10px]">{ev.entity_type}</code> · {String(ev.entity_id).slice(0, 12)}
                        </div>
                      )}
                      <div className="text-[10px] text-muted-foreground/70 mt-0.5 flex items-center gap-1">
                        <span className="w-6 h-6 rounded-md bg-muted flex items-center justify-center text-muted-foreground flex-shrink-0"><meta.icon className="w-3.5 h-3.5" /></span>
                        {ev.at ? (() => {
                          try { return formatDistanceToNow(new Date(ev.at), { addSuffix: true, locale: es }); }
                          catch { return ev.at; }
                        })() : ""}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <NavLink to="/reportes" onClick={() => setOpen(false)}
            className="px-4 py-3 text-xs font-medium text-primary hover:bg-accent/50 flex items-center justify-center gap-1.5 border-t border-border/60">
            Ver reporte completo <ArrowUpRight className="w-3.5 h-3.5" />
          </NavLink>
        </div>
      )}
    </div>
  );
}
